// Field and hub alerts (#31): things that put real farm flying on hold,
// kept light and game-like. People walk into fields, bird flocks drift
// across, new towers go up, pads get blocked and hubs run low on power. One
// ground crew can clear some of them sooner. Each alert carries a short
// real-world fact for the debrief's field notes. Pure, no DOM.

import {
  ALERT_KINDS, ALERT_FIRST_AFTER, FIELDS, HUBS, FLOCK_RADIUS, FLOCK_SPEED, LOW_POWER_CHARGE,
} from '../config/constants.js';

/** Alert state for a shift; `enabled` only for Standard shifts. */
export function createAlerts({
  enabled = false, random = true, interval = 45, max = 2,
} = {}) {
  return {
    enabled,
    random,             // raise alerts by themselves (free play can turn this off, #46)
    interval,           // game seconds between new alerts
    max,                // alerts active at once
    list: [],
    nextIn: ALERT_FIRST_AFTER,
    seq: 1,
    crew: null,         // { alertId, left } while the ground crew is out
    notes: new Set(),   // kinds (and 'wire') met this shift, for the debrief
  };
}

const active = (s) => s.alerts?.list ?? [];

export const personInField = (s, fieldIdx) => active(s).some((a) => a.kind === 'person' && a.fieldIdx === fieldIdx);
export const hubBlocked = (s, hubIdx) => active(s).some((a) => a.kind === 'padBlocked' && a.hubIdx === hubIdx);
export const hubLowPower = (s, hubIdx) => active(s).some((a) => a.kind === 'lowPower' && a.hubIdx === hubIdx);
export const flocks = (s) => active(s).filter((a) => a.kind === 'birds');

/** How fast a hub charges a craft of `power`, as a fraction of normal (low power slows EV charging). */
export const chargeFactor = (s, hubIdx, power) => (power === 'ev' && hubLowPower(s, hubIdx) ? LOW_POWER_CHARGE : 1);

/** The alert's detail line with its field or hub filled in. */
export function alertDetail(a) {
  return ALERT_KINDS[a.kind].detail
    .replace('{field}', a.fieldIdx != null ? FIELDS[a.fieldIdx].name : '')
    .replace('{hub}', a.hubIdx != null ? HUBS[a.hubIdx].name : '')
    .replace('{call}', a.call ?? '')
    .replace('{base}', a.base ?? '')
    .replace('{power}', a.power ?? '')
    .replace('{reason}', a.reason ?? '');
}

// ---- spawning -----------------------------------------------------------

function pick(random, items) {
  return items.length ? items[Math.floor(random() * items.length)] : null;
}

// Where an alert of `kind` could happen right now, or null if nowhere.
function placeFor(s, kind) {
  const list = active(s);
  const fields = s.ops?.fields ?? [];
  const working = new Set((s.ops?.aircraft ?? []).filter((a) => a.task).map((a) => a.task.fieldIdx));
  if (kind === 'person') {
    const open = fields.filter((f) => f.state !== 'sprayed' && !personInField(s, f.idx));
    return pick(s.random, open)?.idx ?? null;
  }
  if (kind === 'tower') {
    // a scouted field nobody is working: it has to be scouted again
    const clear = fields.filter((f) => f.state === 'clear' && !working.has(f.idx));
    return pick(s.random, clear)?.idx ?? null;
  }
  if (kind === 'padBlocked' || kind === 'lowPower') {
    const free = HUBS.map((_, i) => i).filter((i) => !list.some((a) => a.hubIdx === i && a.kind === kind));
    return pick(s.random, free);
  }
  if (kind !== 'birds') return null; // emergencies come from zones.js, not the spawner
  return list.some((a) => a.kind === 'birds') ? null : 0; // one flock at a time
}

function makeAlert(s, kind, place) {
  const a = { id: s.alerts.seq++, kind, left: ALERT_KINDS[kind].seconds, fieldIdx: null, hubIdx: null };
  if (kind === 'person') {
    // walks across the field from one side to the other
    const f = FIELDS[place];
    const fromLeft = s.random() < 0.5;
    const y = f.y + f.h * (0.25 + s.random() * 0.5);
    a.fieldIdx = place;
    a.from = { x: fromLeft ? f.x + 0.01 : f.x + f.w - 0.01, y };
    a.to = { x: fromLeft ? f.x + f.w - 0.01 : f.x + 0.01, y };
    a.x = a.from.x;
    a.y = a.from.y;
  } else if (kind === 'tower') {
    const f = FIELDS[place];
    a.fieldIdx = place;
    a.x = f.x + f.w * (0.2 + s.random() * 0.6);
    a.y = f.y + f.h * (0.2 + s.random() * 0.6);
    const field = s.ops.fields.find((x) => x.idx === place);
    field.state = 'unscouted';
    field.sweep = null;
    field.tower = { x: a.x, y: a.y };
  } else if (kind === 'padBlocked' || kind === 'lowPower') {
    a.hubIdx = place;
    a.x = HUBS[place].x;
    a.y = HUBS[place].y;
  } else {
    // a flock enters at one side and drifts across, through the middle of the farm
    const side = Math.floor(s.random() * 4);
    const t = 0.25 + s.random() * 0.5;
    const start = [{ x: t, y: -0.08 }, { x: 1.08, y: t }, { x: t, y: 1.08 }, { x: -0.08, y: t }][side];
    const aim = { x: 0.35 + s.random() * 0.3, y: 0.35 + s.random() * 0.3 };
    const d = Math.hypot(aim.x - start.x, aim.y - start.y);
    Object.assign(a, {
      x: start.x, y: start.y, r: FLOCK_RADIUS,
      vx: ((aim.x - start.x) / d) * FLOCK_SPEED, vy: ((aim.y - start.y) / d) * FLOCK_SPEED,
      call: 'Bird flock',
    });
  }
  return a;
}

function chooseKind(s) {
  const options = Object.entries(ALERT_KINDS)
    .map(([kind, k]) => ({ kind, weight: k.weight, place: placeFor(s, kind) }))
    .filter((o) => o.place !== null);
  const total = options.reduce((n, o) => n + o.weight, 0);
  let r = s.random() * total;
  for (const o of options) {
    r -= o.weight;
    if (r < 0) return o;
  }
  return options[options.length - 1] ?? null;
}

/** Start an alert now (tests, and the spawner). Returns it, or null when it can't happen. */
export function raiseAlert(s, kind, place = placeFor(s, kind)) {
  if (place === null || place === undefined) return null;
  const a = makeAlert(s, kind, place);
  s.alerts.list.push(a);
  s.alerts.notes.add(kind);
  return a;
}

// ---- ground crew -------------------------------------------------------------

export const crewBusy = (s) => Boolean(s.alerts?.crew);

/** Can the ground crew be sent to this alert right now? */
export function canDispatch(s, alertId) {
  const a = active(s).find((x) => x.id === alertId);
  return Boolean(a) && ALERT_KINDS[a.kind].crew !== null && !crewBusy(s);
}

/** Send the one ground crew to clear an alert sooner. */
export function dispatchCrew(s, alertId) {
  if (!canDispatch(s, alertId)) return false;
  const a = active(s).find((x) => x.id === alertId);
  s.alerts.crew = { alertId, left: ALERT_KINDS[a.kind].crew };
  return true;
}

// ---- stepping ------------------------------------------------------------------

export function clear(s, a, handled, events = []) {
  s.alerts.list = s.alerts.list.filter((x) => x !== a);
  if (s.alerts.crew?.alertId === a.id) s.alerts.crew = null;
  events.push({ kind: 'cleared', alert: a, handled });
}

/**
 * Advance alerts by `dt` game seconds: raise new ones on the shift's pace,
 * move people and flocks, run the crew, and clear what's over. Returns
 * events: { kind: 'raised' | 'cleared', alert, handled? }.
 */
export function stepAlerts(s, dt) {
  const events = [];
  const al = s.alerts;
  if (!al?.enabled || !s.running) return events;

  if (al.random) al.nextIn -= dt;
  if (al.random && al.nextIn <= 0) {
    al.nextIn = al.interval * (0.75 + s.random() * 0.5);
    if (al.list.filter((x) => x.kind !== 'emergency').length < al.max) {
      const choice = chooseKind(s);
      if (choice) events.push({ kind: 'raised', alert: raiseAlert(s, choice.kind, choice.place) });
    }
  }

  if (al.crew) {
    al.crew.left -= dt;
    const target = al.list.find((a) => a.id === al.crew.alertId);
    if (!target) al.crew = null;
    else if (al.crew.left <= 0) clear(s, target, true, events);
  }

  for (const a of [...al.list]) {
    if (a.kind === 'emergency') continue; // zones.js runs its countdown
    if (a.left !== null) a.left -= dt;
    if (a.kind === 'person') {
      const k = 1 - Math.max(0, a.left) / ALERT_KINDS.person.seconds;
      a.x = a.from.x + (a.to.x - a.from.x) * k;
      a.y = a.from.y + (a.to.y - a.from.y) * k;
    }
    if (a.kind === 'birds') {
      a.x += a.vx * dt;
      a.y += a.vy * dt;
      if (a.x < -0.15 || a.x > 1.15 || a.y < -0.15 || a.y > 1.15) { clear(s, a, false, events); continue; }
    }
    if (a.kind === 'tower') {
      const field = s.ops?.fields.find((f) => f.idx === a.fieldIdx);
      // scouted again: the tower stays on the map, marked
      if (!field || field.state !== 'unscouted') {
        if (field?.tower) field.tower.marked = true;
        clear(s, a, false, events);
        continue;
      }
    }
    if (a.left !== null && a.left <= 0) clear(s, a, false, events);
  }
  return events;
}

/**
 * Flocks as moving obstacles for conflict prediction and strikes: each
 * looks like a drone flying straight on, with a radius.
 */
export function flockTracks(s) {
  return flocks(s).map((f) => ({
    id: `flock-${f.id}`, call: 'Bird flock', x: f.x, y: f.y, r: f.r, state: 'enroute', flock: true,
    to: { x: f.x + f.vx * 100, y: f.y + f.vy * 100 }, speed: Math.hypot(f.vx, f.vy),
  }));
}
